// eslint-disable-next-line simple-import-sort/sort
import { Button } from "@material-ui/core";
import DialogContentText from "@material-ui/core/DialogContentText";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import ActionDialog from "@saleor/components/ActionDialog";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import UploadErrorDialog from "@saleor/components/UploadErrorDialog";
import {
  defaultListSettings,
  DEFAULT_INITIAL_PAGINATION_DATA,
  DEFAULT_INITIAL_SEARCH_DATA,
  ProductListColumns
} from "@saleor/config";
import messages from "@saleor/containers/BackgroundTasks/messages";
import { Task } from "@saleor/containers/BackgroundTasks/types";
import useBackgroundTask from "@saleor/hooks/useBackgroundTask";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import useUser from "@saleor/hooks/useUser";
import { commonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import ProductBulkUploadDialog from "@saleor/products/components/ProductBulkUploadDialog";
import ProductExportDialog from "@saleor/products/components/ProductExportDialog";
import {
  getAttributeIdFromColumnValue,
  isAttributeColumnValue
} from "@saleor/products/components/ProductListPage/utils";
import { ProductsUser } from "@saleor/products/customTypes/ProductsUser";
import useBrandUpdate from "@saleor/products/hooks/useBrandUpdate";
import useProductUserType from "@saleor/products/hooks/useProductUserType";
import useTenantUpdate from "@saleor/products/hooks/useTenantUpdate";
import useUserViewUpdate from "@saleor/products/hooks/useUserViewUpdate";
import { ProductListVariables } from "@saleor/products/types/ProductList";
import getProductAddUrlByUserType from "@saleor/products/utils/getProductAddUrlByUserType";
import getProductUpdateUrlByUserType from "@saleor/products/utils/getProductUpdateUrlByUserType";
import useAttributeSearch from "@saleor/searches/useAttributeSearch";
import useBrandPrivateSearch from "@saleor/searches/useBrandPrivateSearch";
import useBrandSearch from "@saleor/searches/useBrandSearch";
import useCategorySearch from "@saleor/searches/useCategorySearch";
import useMallSearch from "@saleor/searches/useMallSearch";
import { useStaffTenantAssignmentQuery } from "@saleor/staff/queries";
import { ListViews } from "@saleor/types";
import {
  ExportProductsInput,
  ExportScope,
  ProductStatusEnum,
  ProductTemplateEnum,
  ProductUploadTemplateTypeEnum,
  ProductUserViewEnum,
  SmopStatusEnum,
  SmopUserTypeCode
} from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import { getSortUrlVariables } from "@saleor/utils/sort";
import { getArrayQueryParam } from "@saleor/utils/urls";
import { useWarehouseList } from "@saleor/warehouses/queries";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ProductListPage from "../../components/ProductListPage";
import {
  useProductBulkDeleteMutation,
  // useProductBulkPublishMutation,
  useProductBulkReadyMutation,
  useProductExport,
  // useProductVisibilityMutation,
  useProductUpload,
  useProductVisibilityBulkUpdateMutation
} from "../../mutations";
import {
  useAvailableInGridAttributesQuery,
  useInitialProductFilterDataQuery,
  useProductListQuery
} from "../../queries";
import {
  productListUrl,
  ProductListUrlDialog,
  ProductListUrlQueryParams,
  ProductListUrlSortField,
  productMainCreateUrl
} from "../../urls";
import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables,
  saveFilterTab
} from "./filters";
import { getSortQueryVariables } from "./sort";
import { useLocation } from "react-router-dom";

interface ProductListProps {
  params: ProductListUrlQueryParams;
}

interface TenantPrivate {
  mallId: string;
  mallName: string;
  tenantId: string;
  tenantName: string;
}

export interface BrandPrivate {
  brandId: string;
  tenants: TenantPrivate[];
}

const useStyles = makeStyles(
  {
    btnDisabled: {
      "& span": {
        color: "#b3b3b3"
      }
    },
    errMessage: {
      background: "#ff4b3e",
      borderRadius: "10px",
      color: "#fff",
      margin: "10px 20px",
      padding: "8px 10px",
      position: "absolute",
      right: 0,
      top: "90px",
      zIndex: 9
    }
  },
  { name: "ProductList" }
);

export const ProductList: React.FC<ProductListProps> = ({ params }) => {
  const classes = useStyles(params);
  const navigate = useNavigator();
  const notify = useNotifier();
  const paginate = usePaginator();
  const { queue } = useBackgroundTask();
  const [clearPagination, setOnClearPagination] = useState(false);
  const [samePublished, setSamePublished] = useState(false);
  const [initSelection, setInitSelection] = useState(false);
  const [initSelectionReady, setInitSelectionReady] = useState("");
  const [sameStatus, setSameStatus] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadErrorLink, setUploadErrorLink] = React.useState(null);
  const [tenantWarehouse, setTenantWarehouse] = useState<string[]>([]);
  const shop = useShop();
  const { user: userDetail } = useUser();
  const [selectedProducts, setSelectedProducts] = React.useState([]);
  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    getArrayQueryParam(selectedProducts)
  );

  const handleSelectProducts = (productIds, isChecked) => {
    const selectedIds = new Set(selectedProducts);
    productIds.forEach(id => {
      if (isChecked && !selectedIds.has(id)) {
        selectedIds.add(id);
      }

      if (!isChecked && selectedIds.has(id)) {
        selectedIds.delete(id);
      }
    });

    setSelectedProducts(Array.from(selectedIds));
  };

  const { updateListSettings, settings } = useListSettings<ProductListColumns>(
    ListViews.PRODUCT_LIST
  );
  // Hook for updating selected KAMS user view
  const [onUserViewChange, userView, userViews] = useUserViewUpdate(
    userDetail,
    params
  );

  // const {
  //   location: { search }
  // } = useRouter();
  const location = useLocation();
  const productsUser = useProductUserType(params);

  const isAccountManager = productsUser.isKAMS;
  const isSellerMaster =
    productsUser.userType === ProductUserViewEnum.SELLER_MASTER;
  const isStoreAdmin =
    productsUser.userType === ProductUserViewEnum.STORE_ADMIN;

  const brandInitialSearchData = {
    after: null,
    first: 20,
    query: ""
  };

  const {
    loadMore: loadMoreBrand,
    search: searchBrands,
    result: brandsResult
  } = useBrandPrivateSearch({
    skip: !isAccountManager,
    variables: {
      ...brandInitialSearchData,
      accountManager: userDetail.id
    }
  });

  const brands = maybe(() => brandsResult?.data?.search?.edges, []).map(
    edge => edge.node
  );

  const tenantsPrivate = maybe(() => brandsResult?.data?.search?.edges, [])
    .map(edge => edge.node.tenants)
    .flat();

  // Hook for updating of brands
  const [onBrandChange, brand, brandChoices] = useBrandUpdate(
    params,
    brands,
    brandsResult.loading
  );

  // Hook for mapping stores
  const [onTenantChange, tenant, tenantChoices] = useTenantUpdate(
    tenantsPrivate || [],
    brand.value,
    userView.value,
    params
  );

  useEffect(() => {
    if (
      userView.value === SmopUserTypeCode.STORE_ADMIN &&
      brand.value &&
      tenant.value.tenantId
    ) {
      navigate(
        productListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA,
          brand: brand.value,
          mallId: tenant.value.mallId,
          tenant: tenant.value.tenantId,
          viewAs: userView.value
        })
      );
    } else if (
      userView.value === SmopUserTypeCode.SELLER_MASTER &&
      brand.value
    ) {
      navigate(
        productListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA,
          brand: brand.value,
          viewAs: userView.value
        })
      );
    }
  }, [tenant.value.tenantId, brand.value, userView.value]);

  useEffect(() => {
    if (productsUser.isKAMS && location.search === "") {
      navigate(
        productListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA,
          brand: brand.value,
          viewAs: userView.value
        })
      );
    }
  }, [location.search]);

  const intl = useIntl();
  const { data: initialFilterData } = useInitialProductFilterDataQuery({
    skip: isAccountManager,
    variables: {
      categories: params.categories
    }
  });
  const searchCategories = useCategorySearch({
    skip: isAccountManager,
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      first: 5
    }
  });
  const searchAttributes = useAttributeSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      first: 10
    }
  });
  const warehouses = useWarehouseList({
    variables: {
      first: 100
    }
  });

  const searchMall = useMallSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      brandId:
        userDetail.userType.code === SmopUserTypeCode.SELLER_MASTER
          ? userDetail.accessGroup.accesses[0].objectId
          : null,
      first: 50,
      status: SmopStatusEnum.ACT
    }
  });

  useEffect(
    () =>
      navigate(
        productListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA
        }),
        true
      ),
    [settings.rowNumber]
  );

  useEffect(() => {
    navigate(
      productListUrl({
        ...params,
        ...DEFAULT_INITIAL_PAGINATION_DATA,
        after: null,
        query: null
      }),
      true
    );
    setOnClearPagination(false);
  }, [clearPagination === true]);

  useEffect(() => {
    let formattedProducts = [];
    const newProducts =
      data && data.products
        ? data.products.edges.filter(
            edge =>
              listElements.includes(edge.node.id) ||
              selectedProducts.includes(edge.node.id)
          )
        : [];
    formattedProducts = newProducts.map(product =>
      product.node
        ? {
            checkPublished:
              product.node &&
              product.node.productVisibility &&
              product.node.productVisibility.isVisible
                ? true
                : false,
            id: product.node.id,
            isAvailable: product.node.isAvailable,
            isPublish: product.node.isPublished,
            productStatus: product.node.productStatus.toLowerCase(),
            productVisibility: product.node.productVisibility
          }
        : ""
    );
    const initChecked =
      formattedProducts &&
      formattedProducts[0] &&
      formattedProducts[0].checkPublished
        ? formattedProducts[0].checkPublished
        : false;
    setInitSelection(initChecked);
    const initCheckedReady =
      formattedProducts &&
      formattedProducts[0] &&
      formattedProducts[0].productStatus
        ? formattedProducts[0].productStatus
        : false;
    setInitSelectionReady(initCheckedReady);
    setSamePublished(compareProducts(formattedProducts, "published"));
    setSameStatus(compareProducts(formattedProducts, "status"));
  }, [listElements, selectedProducts]);

  const compareProducts = (products, compareTo) => {
    for (let i = 1; i < products.length; i++) {
      switch (compareTo) {
        case "published":
          if (products[i].checkPublished !== products[0].checkPublished) {
            return false;
          }
          break;
        case "status":
          if (products[i].productStatus !== products[0].productStatus) {
            return false;
          }
          break;
      }
    }
    return true;
  };
  const tabs = getFilterTabs();

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 10);

  const [openModal, closeModal] = createDialogActionHandlers<
    ProductListUrlDialog,
    ProductListUrlQueryParams
  >(navigate, productListUrl, params);

  const [exportProducts, exportProductsOpts] = useProductExport({
    onCompleted: data => {
      if (data.exportProducts.errors.length === 0) {
        notify({
          text: intl.formatMessage({
            defaultMessage:
              "We are currently exporting your requested CSV. As soon as it is available it will be sent to your email address"
          }),
          title: intl.formatMessage({
            defaultMessage: "Exporting CSV",
            description: "waiting for export to end, header"
          })
        });
        queue(Task.EXPORT, {
          id: data.exportProducts.exportFile.id
        });
        closeModal();
        reset();
      }
    }
  });

  const [uploadProduct, uploadProductOpts] = useProductUpload({
    onCompleted: data => {
      if (data.productUploadTemplate.productErrors.length === 0) {
        queue(Task.UPLOAD, {
          id: data.productUploadTemplate.productUpload.id,
          onResponseData: jobData => {
            if (jobData && jobData.data) {
              if (jobData.data.productUploadJob.status === "WITH_ERROR_FILE") {
                notify({
                  status: "error",
                  text: intl.formatMessage(commonMessages.uploadError),
                  title: intl.formatMessage(messages.uploadFailedTitle)
                });
                setUploadErrorLink(
                  jobData.data?.productUploadJob?.errorFileUrl
                );
              }
              if (jobData.data.productUploadJob.status === "EMPTY_FILE") {
                notify({
                  status: "error",
                  text: intl.formatMessage(commonMessages.uploadEmptyError),
                  title: intl.formatMessage(messages.uploadFailedTitle)
                });
              }
              if (jobData.data.productUploadJob.status !== "PENDING") {
                refetch();
                setUploadLoading(false);
                closeModal();
                reset();
              }
            } else {
              setUploadLoading(false);
              closeModal();
              reset();
              notify({
                status: "error",
                text: intl.formatMessage(commonMessages.somethingWentWrong),
                title: intl.formatMessage(messages.uploadFailedTitle)
              });
            }
          }
        });
      } else {
        notify({
          status: "error",
          text: data.productUploadTemplate.productErrors[0].message,
          title: intl.formatMessage(messages.uploadFailedTitle)
        });
        setUploadLoading(false);
        closeModal();
        reset();
      }
    }
  });

  const [
    changeFilters,
    resetFilters,
    handleSearchChange
  ] = createFilterHandlers({
    cleanupFn: reset,
    createUrl: productListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const resetFiltersHandler = (
    productsUser: ProductsUser,
    params,
    resetFilters
  ) => {
    if (productsUser.isKAMS) {
      return () => {
        reset();
        navigate(
          productListUrl({
            ...params,
            ...DEFAULT_INITIAL_PAGINATION_DATA,
            brand: params.brand,
            viewAs: params.viewAs
          })
        );
      };
    } else {
      return resetFilters;
    }
  };

  const handleTabChange = (tab: number) => {
    reset();
    navigate(
      productListUrl({
        activeTab: tab.toString(),
        ...getFilterTabs()[tab - 1].data
      })
    );
  };

  const handleFilterTabDelete = () => {
    deleteFilterTab(currentTab);
    reset();
    navigate(productListUrl());
  };

  const handleFilterTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(tabs.length + 1);
  };

  const handleSort = (field: ProductListUrlSortField, attributeId?: string) =>
    navigate(
      productListUrl({
        ...params,
        ...getSortUrlVariables(field, params),
        attributeId,
        ...DEFAULT_INITIAL_PAGINATION_DATA
      })
    );

  const { data: tenantData } = useStaffTenantAssignmentQuery({
    skip: userDetail.userType.code !== "STORE_ADMIN",
    variables: {
      id: userDetail.userType.accesses[0]?.objectId
    }
  });

  useEffect(() => {
    if (tenantData) {
      setTenantWarehouse([tenantData.tenant?.mall?.firstWarehouse?.id || ""]);
    }
  }, [tenantData]);

  const createNewFilter = (data: ExportProductsInput) => {
    if (userDetail.userType.code === SmopUserTypeCode.STORE_ADMIN) {
      return {
        ...data.exportInfo,
        warehouses: tenantWarehouse
      };
    }

    return data.exportInfo;
  };

  const paginationState = createPaginationState(settings.rowNumber, params);
  const currencySymbol = maybe(() => shop.defaultCurrency, "USD");
  const filter = getFilterVariables(params, tenantWarehouse);
  const sort = getSortQueryVariables(params);

  // const countAllProducts = useCountAllProducts({
  //   skip: isAccountManager && (brand.value === undefined || brand.value === ""),
  //   variables: {
  //     ...(isAccountManager && {
  //       ...(userView.value === SmopUserTypeCode.STORE_ADMIN && {
  //         mallId: tenant.value.mallId
  //       }),
  //       userViewInfo: {
  //         brandId: params.brand ? params.brand : brand.value,
  //         tenantId:
  //           userView.value === SmopUserTypeCode.STORE_ADMIN && tenant
  //             ? tenant.value.tenantId
  //             : "",
  //         viewAs: userView.value as ProductUserViewEnum
  //       }
  //     })
  //   }
  // });

  const queryVariables = React.useMemo<ProductListVariables>(
    () => ({
      ...paginationState,
      filter,
      sort,
      ...(isAccountManager && {
        ...(userView.value === SmopUserTypeCode.STORE_ADMIN && {
          mallId: tenant.value.mallId
        }),
        userViewInfo: {
          brandId: params.brand ? params.brand : brand.value,
          tenantId:
            userView.value === SmopUserTypeCode.STORE_ADMIN && tenant
              ? tenant.value.tenantId
              : "",
          viewAs: userView.value as ProductUserViewEnum
        }
      })
    }),
    [
      params,
      settings.rowNumber,
      userView.value,
      tenant.value.tenantId,
      brand.value
    ]
  );
  const { data, loading, refetch } = useProductListQuery({
    displayLoader: true,
    skip: isAccountManager && (brand === undefined || brand.value === ""),
    variables: queryVariables
  });

  function filterColumnIds(columns: ProductListColumns[]) {
    return columns
      .filter(isAttributeColumnValue)
      .map(getAttributeIdFromColumnValue);
  }
  const attributes = useAvailableInGridAttributesQuery({
    variables: { first: 6, ids: filterColumnIds(settings.columns) }
  });

  const [
    ProductVisibilityBulkUpdate,
    productBulkPublishUpdate
  ] = useProductVisibilityBulkUpdateMutation({
    onCompleted: data => {
      const { productErrors } = data.productVisibilityBulkUpdate;
      if (productErrors.length === 0) {
        closeModal();
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
        reset();
        refetch();
      }
    }
  });

  const [
    productStatusBulkUpdate,
    productStatusBulkUpdateOpts
  ] = useProductBulkReadyMutation({
    onCompleted: data => {
      if (sameStatus && data.productStatusBulkUpdate.errors.length === 0) {
        closeModal();
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
        reset();
        refetch();
      } else if (
        !sameStatus &&
        data.productStatusBulkUpdate.errors.length === 0
      ) {
        closeModal();
        notify({
          status: "error",
          text: data.productStatusBulkUpdate.errors[0].message
        });
        reset();
        refetch();
      } else if (data.productStatusBulkUpdate.errors.length !== 0) {
        closeModal();
        notify({
          status: "error",
          text: data.productStatusBulkUpdate.errors[0].message
        });
        reset();
        refetch();
      }
    }
  });

  const filterOpts = getFilterOpts(
    params,
    maybe(() => initialFilterData.attributes.edges.map(edge => edge.node), []),
    {
      initial: maybe(
        () => initialFilterData.categories.edges.map(edge => edge.node),
        []
      ),
      search: searchCategories
    }
  );

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.products.pageInfo),
    paginationState,
    params
  );

  const [
    productBulkDelete,
    productBulkDeleteOpts
  ] = useProductBulkDeleteMutation({
    onCompleted: data => {
      if (data.productBulkDelete.errors.length === 0) {
        closeModal();
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
        reset();
        refetch();
      } else if (data.productBulkDelete.errors.length !== 0) {
        closeModal();
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage:
              "Cannot Delete Ready/Unlisted items. Please check selected item/s."
          })
        });
        reset();
        refetch();
      }
    }
  });

  const { result: productUploadBrands } = useBrandSearch({
    skip: !productsUser.isKAMS,
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      accountManager: productsUser.id
    }
  });

  const kamsProductUploadBrands = maybe(
    () => productUploadBrands?.data?.search?.edges,
    []
  ).map(edge => edge.node);

  return (
    <>
      {!samePublished || !sameStatus ? (
        <div className={classes.errMessage}>
          <strong>Note:</strong> Different status selected. Please check
          selected item/s to proceed with action.
        </div>
      ) : (
        ""
      )}
      <ProductListPage
        productsUser={productsUser}
        params={params}
        onChangeType={typeValue =>
          navigate(productListUrl({ ...params, type: typeValue }))
        }
        onClearPagination={setOnClearPagination}
        activeAttributeSortId={params.attributeId}
        brand={brand.value}
        brands={brandChoices}
        brandLabel={brand.label}
        isAccountManager={isAccountManager}
        isSellerMaster={isSellerMaster}
        isStoreAdmin={isStoreAdmin}
        onBrandChange={onBrandChange}
        tenant={tenant.value.tenantId}
        tenants={tenantChoices}
        tenantLabel={tenant.label}
        onTenantChange={onTenantChange}
        onUserViewChange={onUserViewChange}
        userView={userView.value}
        userViewLabel={userView.label}
        userViews={userViews}
        sort={{
          asc: params.asc,
          sort: params.sort
        }}
        onSort={handleSort}
        availableInGridAttributes={maybe(
          () => attributes.data.availableInGrid.edges.map(edge => edge.node),
          []
        )}
        currencySymbol={currencySymbol}
        currentTab={currentTab}
        defaultSettings={defaultListSettings[ListViews.PRODUCT_LIST]}
        fetchBrands={query => searchBrands(query)}
        fetchMoreBrands={{
          hasMore: maybe(() => brandsResult.data.search.pageInfo.hasNextPage),
          loading: brandsResult.loading,
          onFetchMore: loadMoreBrand
        }}
        fetchTenants={() => ""}
        filterOpts={filterOpts}
        gridAttributes={maybe(
          () => attributes.data.grid.edges.map(edge => edge.node),
          []
        )}
        userDetail={userDetail}
        totalGridAttributes={maybe(
          () => attributes?.data?.availableInGrid?.totalCount,
          0
        )}
        settings={settings}
        loading={attributes.loading}
        handleSelectProducts={handleSelectProducts}
        selectedProducts={selectedProducts}
        hasMore={maybe(
          () => attributes.data.availableInGrid.pageInfo.hasNextPage,
          false
        )}
        onAdd={() =>
          navigate(
            getProductAddUrlByUserType(
              productsUser,
              productMainCreateUrl,
              params
            )
          )
        }
        disabled={loading}
        products={maybe(() => data.products.edges.map(edge => edge.node))}
        onFetchMore={() =>
          attributes.loadMore(
            (prev, next) => {
              if (
                prev.availableInGrid.pageInfo.endCursor ===
                next.availableInGrid.pageInfo.endCursor
              ) {
                return prev;
              }
              return {
                ...prev,
                availableInGrid: {
                  ...prev.availableInGrid,
                  edges: [
                    ...prev.availableInGrid.edges,
                    ...next.availableInGrid.edges
                  ],
                  pageInfo: next.availableInGrid.pageInfo
                }
              };
            },
            {
              after: attributes.data.availableInGrid.pageInfo.endCursor
            }
          )
        }
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onUpdateListSettings={updateListSettings}
        pageInfo={pageInfo}
        onRowClick={id => () => {
          navigate(getProductUpdateUrlByUserType(productsUser, id, params));
        }}
        onAll={resetFiltersHandler(productsUser, params, resetFilters)}
        searchBy={searchBy => navigate(productListUrl({ ...params, searchBy }))}
        toolbar={
          <>
            {isStoreAdmin ? (
              <>
                <Button
                  color="primary"
                  className={
                    !initSelection || !samePublished ? classes.btnDisabled : ""
                  }
                  disabled={!initSelection || !samePublished}
                  onClick={() =>
                    openModal("unpublish", {
                      ids: listElements
                    })
                  }
                >
                  <FormattedMessage
                    defaultMessage="Unpublish"
                    description="unpublish product, button"
                  />
                </Button>
                <Button
                  color="primary"
                  className={
                    initSelection || !samePublished ? classes.btnDisabled : ""
                  }
                  disabled={initSelection || !samePublished}
                  onClick={() =>
                    openModal("publish", {
                      ids: listElements
                    })
                  }
                >
                  <FormattedMessage
                    defaultMessage="Publish"
                    description="publish product, button"
                  />
                </Button>
              </>
            ) : (
              <>
                <Button
                  color="primary"
                  className={
                    (initSelectionReady !== "unlisted" &&
                      initSelectionReady !== "draft") ||
                    !sameStatus
                      ? classes.btnDisabled
                      : ""
                  }
                  disabled={
                    (initSelectionReady !== "unlisted" &&
                      initSelectionReady !== "draft") ||
                    !sameStatus
                  }
                  onClick={() =>
                    openModal("ready", {
                      ids: listElements
                    })
                  }
                >
                  <FormattedMessage
                    defaultMessage="Ready"
                    description="ready product, button"
                  />
                </Button>
                <Button
                  color="primary"
                  className={
                    (initSelectionReady !== "ready" &&
                      initSelectionReady !== "draft") ||
                    !sameStatus
                      ? classes.btnDisabled
                      : ""
                  }
                  disabled={
                    (initSelectionReady !== "ready" &&
                      initSelectionReady !== "draft") ||
                    !sameStatus
                  }
                  onClick={() =>
                    openModal("unlisted", {
                      ids: listElements
                    })
                  }
                >
                  <FormattedMessage
                    defaultMessage="unlisted"
                    description="unlisted product, button"
                  />
                </Button>
                <IconButton
                  color="primary"
                  onClick={() =>
                    openModal("delete", {
                      ids: listElements
                    })
                  }
                >
                  <DeleteIcon />
                </IconButton>
              </>
            )}
          </>
        }
        isChecked={isSelected}
        selected={listElements.length}
        toggle={toggle}
        toggleAll={toggleAll}
        onSearchChange={handleSearchChange}
        onFilterChange={changeFilters}
        onTabSave={() => openModal("save-search")}
        onTabDelete={() => openModal("delete-search")}
        onTabChange={handleTabChange}
        initialSearch={params.query || ""}
        tabs={getFilterTabs().map(tab => tab.name)}
        onExport={() => openModal("export")}
        onUpload={path => openModal(path)}
        totalCount={maybe(() => data.products.totalCount)}
      />
      <ActionDialog
        open={params.action === "delete"}
        confirmButtonState={productBulkDeleteOpts.status}
        onClose={closeModal}
        onConfirm={() =>
          productBulkDelete({
            variables: { ids: Object.values(params.ids) }
          })
        }
        title={intl.formatMessage({
          defaultMessage: "Delete Products",
          description: "dialog header"
        })}
        variant="delete"
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="{counter,plural,one{Are you sure you want to delete this product?} other{Are you sure you want to delete {displayQuantity} products?}}"
            description="dialog content"
            values={{
              counter: maybe(() => params.ids.length),
              displayQuantity: <strong>{maybe(() => params.ids.length)}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>
      <ActionDialog
        open={params.action === "publish"}
        confirmButtonState={productBulkPublishUpdate.status}
        onClose={closeModal}
        onConfirm={() =>
          ProductVisibilityBulkUpdate({
            variables: {
              ids: Object.values(params.ids),
              isVisible: true
            }
          })
        }
        title={intl.formatMessage({
          defaultMessage: "Publish Products",
          description: "dialog header"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="{counter,plural,one{Are you sure you want to publish this product?} other{Are you sure you want to publish {displayQuantity} products?}}"
            description="dialog content"
            values={{
              counter: maybe(() => params.ids.length),
              displayQuantity: <strong>{maybe(() => params.ids.length)}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>
      <ActionDialog
        open={params.action === "unpublish"}
        confirmButtonState={productBulkPublishUpdate.status}
        onClose={closeModal}
        onConfirm={() =>
          ProductVisibilityBulkUpdate({
            variables: {
              ids: Object.values(params.ids),
              isVisible: false
            }
          })
        }
        title={intl.formatMessage({
          defaultMessage: "Unpublish Products",
          description: "dialog header"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="{counter,plural,one{Are you sure you want to unpublish this product?} other{Are you sure you want to unpublish {displayQuantity} products?}}"
            description="dialog content"
            values={{
              counter: maybe(() => params.ids.length),
              displayQuantity: <strong>{maybe(() => params.ids.length)}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>
      <ActionDialog
        open={params.action === "ready"}
        confirmButtonState={productStatusBulkUpdateOpts.status}
        onClose={closeModal}
        onConfirm={() =>
          productStatusBulkUpdate({
            variables: {
              ids: Object.values(params.ids),
              productStatus: ProductStatusEnum.READY
            }
          })
        }
        title={intl.formatMessage({
          defaultMessage: "Ready Products",
          description: "dialog header"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="{counter,plural,one{Are you sure you want to ready this product?} other{Are you sure you want to ready {displayQuantity} products?}}"
            description="dialog content"
            values={{
              counter: maybe(() => params.ids.length),
              displayQuantity: <strong>{maybe(() => params.ids.length)}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>
      <ActionDialog
        open={params.action === "unlisted"}
        confirmButtonState={productStatusBulkUpdateOpts.status}
        onClose={closeModal}
        onConfirm={() =>
          productStatusBulkUpdate({
            variables: {
              ids: Object.values(params.ids),
              productStatus: ProductStatusEnum.UNLISTED
            }
          })
        }
        title={intl.formatMessage({
          defaultMessage: "Unlist Products",
          description: "dialog header"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="{counter,plural,one{Are you sure you want to unlist this product?} other{Are you sure you want to unlist {displayQuantity} products?}}"
            description="dialog content"
            values={{
              counter: maybe(() => selectedProducts.length),
              displayQuantity: (
                <strong>{maybe(() => selectedProducts.length)}</strong>
              )
            }}
          />
        </DialogContentText>
      </ActionDialog>
      <ProductExportDialog
        productsUser={productsUser}
        attributes={(searchAttributes.result.data?.search.edges || []).map(
          edge => edge.node
        )}
        hasMore={searchAttributes.result.data?.search.pageInfo.hasNextPage}
        loading={searchAttributes.result.loading}
        onFetch={searchAttributes.search}
        onFetchMore={searchAttributes.loadMore}
        open={params.action === "export"}
        confirmButtonState={exportProductsOpts.status}
        errors={exportProductsOpts.data?.exportProducts.errors || []}
        malls={(searchMall.result.data?.search.edges || []).map(
          edge => edge.node
        )}
        selectedProducts={listElements.length}
        warehouses={
          warehouses.data?.warehouses.edges.map(edge => edge.node) || []
        }
        onClose={closeModal}
        onSubmit={data => {
          let currentScope = data.scope;
          let currentFilter = filter;
          if (
            userDetail.userType.code === SmopUserTypeCode.SELLER_MASTER &&
            (data.exportInfo.template ===
              ProductTemplateEnum.INVENTORY_AND_STATUS ||
              data.exportInfo.template ===
                ProductTemplateEnum.RETAIL_AND_SELLING_PRICE)
          ) {
            if (data.scope === ExportScope.IDS) {
              currentScope = ExportScope.IDS_AND_FILTER;
            } else {
              currentScope = ExportScope.FILTER;
            }
            currentFilter = data.filter;
          }
          exportProducts({
            variables: {
              input: {
                ...data,
                exportInfo: createNewFilter(data),
                filter: currentFilter,
                ids: listElements,
                scope: currentScope
              }
            }
          });
        }}
      />
      <ProductBulkUploadDialog
        productsUser={productsUser}
        brands={kamsProductUploadBrands}
        open={
          params.action === "uploadBulkPrice" ||
          params.action === "uploadBulkImage" ||
          params.action === "uploadBulkProduct" ||
          params.action === "uploadBulkInventory"
        }
        action={params.action}
        loading={uploadProductOpts.loading || uploadLoading}
        onClose={closeModal}
        onFileError={() => {
          notify({
            status: "error",
            text: intl.formatMessage({
              defaultMessage: "Please upload XLSX files only."
            }),
            title: intl.formatMessage({
              defaultMessage: "Can't upload file!",
              description: "import, header"
            })
          });
        }}
        onSubmit={(getFile, isOverwrite, productsUser, brand) => {
          setUploadLoading(true);
          let templateType = ProductUploadTemplateTypeEnum.PRODUCT_FILE;
          switch (params.action) {
            case "uploadBulkPrice":
              templateType = ProductUploadTemplateTypeEnum.PRICE_UPDATE;
              break;
            case "uploadBulkImage":
              templateType =
                ProductUploadTemplateTypeEnum.PRODUCT_DETAILS_UPDATE;
              break;
            case "uploadBulkInventory":
              templateType = ProductUploadTemplateTypeEnum.INVENTORY_UPDATE;
            default:
              break;
          }
          uploadProduct({
            variables: {
              file: getFile,
              overwrite: isOverwrite,
              templateType,
              ...(productsUser.isKAMS &&
                brand !== undefined && {
                  brand,
                  uploadAs: productsUser.userType as ProductUserViewEnum
                })
            }
          });
        }}
      />
      <UploadErrorDialog
        open={uploadErrorLink}
        onClose={() => setUploadErrorLink(null)}
        uploadErrorLink={uploadErrorLink}
        loading={uploadProductOpts.loading || uploadLoading}
      />
      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleFilterTabSave}
      />
      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleFilterTabDelete}
        tabName={maybe(() => tabs[currentTab - 1].name, "...")}
      />
    </>
  );
};
export default ProductList;
